/**
 * Singleton authentication manager
 * Handles token lifecycle, automatic refresh, and auth state
 */

import { ApiClient } from './ApiClient.js';

export class Auth {
    private static instance: Auth | null = null;
    private static refreshPromise: Promise<string | null> | null = null;
    private apiClient: ApiClient;

    private constructor() {
        this.apiClient = new ApiClient();
    }

    private static getInstance(): Auth {
        if (!Auth.instance) {
            Auth.instance = new Auth();
        }
        return Auth.instance;
    }

    /**
     * Get a valid access token, refreshing if necessary
     * Returns null if user is not authenticated
     */
    public static async getValidAccessToken(): Promise<string | null> {
        const instance = Auth.getInstance();

        // Check for existing token in sessionStorage
        const existingToken = sessionStorage.getItem('access_token');
        if (existingToken && Auth.isTokenValid(existingToken)) {
            return existingToken;
        }

        // Token expired or doesn't exist - clear it
        if (existingToken) {
            Auth.clearAccessToken();
        }

        // If refresh already in progress, wait for it
        if (Auth.refreshPromise) {
            return Auth.refreshPromise;
        }

        // Attempt to refresh token using httpOnly cookie
        Auth.refreshPromise = instance.attemptRefresh();
        const token = await Auth.refreshPromise;
        Auth.refreshPromise = null;

        return token;
    }

    /**
     * Check if JWT token is valid (not expired)
     * Returns false if token is malformed or expired
     */
    private static isTokenValid(token: string): boolean {
        try {
            // Decode JWT payload (format: header.payload.signature)
            const parts = token.split('.');
            if (parts.length !== 3) {
                return false;
            }

            // Decode base64url payload
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

            // Check expiry (exp is in seconds, Date.now() is in milliseconds)
            if (!payload.exp) {
                return false;
            }

            const now = Math.floor(Date.now() / 1000);
            const expiresAt = payload.exp;

            // Token is valid if it hasn't expired yet
            // Add small buffer (10 seconds) to refresh before actual expiry
            return expiresAt > (now + 10);
        } catch (error) {
            console.error('Token validation error:', error);
            return false;
        }
    }

    /**
     * Attempt to refresh the access token using the refresh_token cookie
     */
    private async attemptRefresh(): Promise<string | null> {
        try {
            // Get CSRF token first
            const csrfToken = await this.apiClient.getCsrfToken();

            // Call refresh endpoint (uses httpOnly cookie automatically)
            const response = await fetch('/api/auth/refresh', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken,
                },
                credentials: 'include',
            });

            if (!response.ok) {
                return null;
            }

            const data = await response.json() as {
                success: boolean;
                data?: { access_token: string };
            };

            if (!data.success || !data.data?.access_token) {
                return null;
            }

            // Store the new access token
            const accessToken = data.data.access_token;
            sessionStorage.setItem('access_token', accessToken);

            return accessToken;
        } catch (error) {
            console.error('Token refresh failed:', error);
            return null;
        }
    }

    /**
     * Store access token in sessionStorage
     * Also sets a localStorage hint that survives tab close (for Safari workaround)
     */
    public static storeAccessToken(token: string): void {
        sessionStorage.setItem('access_token', token);
        Auth.setSessionHint();
    }

    /**
     * Clear access token from sessionStorage
     */
    public static clearAccessToken(): void {
        sessionStorage.removeItem('access_token');
    }

    /**
     * Set session hint in localStorage
     * Used to detect if user was previously logged in (survives tab close)
     */
    public static setSessionHint(): void {
        localStorage.setItem('session_hint', '1');
    }

    /**
     * Clear session hint from localStorage
     */
    public static clearSessionHint(): void {
        localStorage.removeItem('session_hint');
    }

    /**
     * Check if session hint exists
     */
    public static hasSessionHint(): boolean {
        return localStorage.getItem('session_hint') === '1';
    }

    /**
     * Logout user - clear local token and call logout endpoint
     */
    public static async logout(): Promise<void> {
        const instance = Auth.getInstance();

        try {
            await instance.apiClient.logout();
        } catch (error) {
            // Even if logout fails on server, clear local state
            console.error('Logout error:', error);
        } finally {
            Auth.clearAccessToken();
            Auth.clearSessionHint();
        }
    }

    /**
     * Redirect to login page
     */
    public static redirectToLogin(): void {
        window.location.href = '/auth/login';
    }
}
